import React from 'react';
import { twMerge } from 'tailwind-merge';
import { XIcon, type Icon } from '@phosphor-icons/react';

type BannerVariant = 'info' | 'warning' | 'error' | 'success';

const VARIANT_CLASSES: Record<BannerVariant, string> = {
  info: 'bg-blue-50 text-blue-700',
  warning: 'bg-amber-50 text-amber-700',
  error: 'bg-red-50 text-red-700',
  success: 'bg-green-50 text-green-700'
};

const CTA_HOVER_CLASSES: Record<BannerVariant, string> = {
  info: 'hover:bg-blue-100 hover:text-blue-900',
  warning: 'hover:bg-amber-100 hover:text-amber-900',
  error: 'hover:bg-red-100 hover:text-red-900',
  success: 'hover:bg-green-100 hover:text-green-900'
};

export interface BannerCta {
  label: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
}

export interface BannerProps {
  variant?: BannerVariant;
  icon?: Icon;
  iconProps?: React.ComponentProps<Icon>;
  children: React.ReactNode;
  cta?: BannerCta;
  className?: string;
  ctaClassName?: string;
  onClick?: () => void;
  /** When set, shows a dismiss control. The caller is responsible for hiding the banner. */
  onDismiss?: () => void;
  dismissAriaLabel?: string;
}

export default function Banner({
  variant = 'info',
  icon: IconComponent,
  iconProps,
  children,
  cta,
  className,
  ctaClassName,
  onClick,
  onDismiss,
  dismissAriaLabel = 'Dismiss'
}: BannerProps) {
  const classNames = twMerge(
    'flex items-center justify-between border border-border-default gap-2 px-4 py-2 rounded-md text-xs min-h-8 shrink-0 text-left',
    VARIANT_CLASSES[variant],
    onClick && 'cursor-pointer',
    className
  );

  const body = (
    <>
      <div className="flex items-center gap-2 min-w-0 flex-1">
        {IconComponent && <IconComponent size={14} weight="light" className="shrink-0" {...iconProps} />}
        <span className="min-w-0">{children}</span>
      </div>

      {cta && (
        <button
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            cta.onClick();
          }}
          disabled={cta.disabled}
          className={twMerge(
            'shrink-0 self-stretch flex items-center rounded px-2 font-medium transition-colors disabled:opacity-50',
            CTA_HOVER_CLASSES[variant],
            ctaClassName
          )}
        >
          {cta.label}
        </button>
      )}

      {onDismiss && (
        <button
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            onDismiss();
          }}
          aria-label={dismissAriaLabel}
          className={twMerge(
            'shrink-0 self-stretch flex items-center rounded px-1.5 transition-colors',
            CTA_HOVER_CLASSES[variant]
          )}
        >
          <XIcon size={14} />
        </button>
      )}
    </>
  );

  return (
    <div
      className={classNames}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={
        onClick
          ? (event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                onClick();
              }
            }
          : undefined
      }
    >
      {body}
    </div>
  );
}
